/* =========================================================
   Motion layer
   Requires (optional) Lenis loaded before this file:
   https://cdn.jsdelivr.net/npm/lenis@1.1.13/dist/lenis.min.js
   Falls back to native smooth scroll if Lenis is missing.
   ========================================================= */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return; // static, fully visible page

  var root = document.documentElement;
  root.classList.add('js-motion');

  /* ---------- 1. Smooth scrolling (Lenis) ---------- */
  var lenis = null;
  var headerOffset = -90;

  if (typeof window.Lenis === 'function') {
    lenis = new window.Lenis({
      duration: 1.15,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true
    });
    (function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    })(performance.now());
  }

  // Anchor links scroll smoothly and account for the sticky header
  document.querySelectorAll('a[href^="#"]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      var id = link.getAttribute('href');
      if (id.length < 2) return;
      var target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      if (lenis) {
        lenis.scrollTo(target, { offset: headerOffset });
      } else {
        var y = target.getBoundingClientRect().top + window.scrollY + headerOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
      // Move keyboard focus to the section for accessibility
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  });

  /* ---------- 2. Hero load sequence ---------- */
  var hero = document.querySelector('.hero');
  var h1 = hero && hero.querySelector('h1');

  if (h1) {
    var words = h1.textContent.trim().split(/\s+/);
    h1.setAttribute('aria-label', h1.textContent.trim());
    h1.innerHTML = words.map(function (w, i) {
      return '<span class="word" aria-hidden="true"><span style="--i:' + i + '">' + w + '</span></span>';
    }).join(' ');
  }

  function startHero() {
    if (!hero) return;
    requestAnimationFrame(function () {
      hero.classList.add('is-loaded');
      // after the intro, let parallax drive the image without transition lag
      setTimeout(function () { hero.classList.add('is-settled'); }, 2500);
    });
  }
  if (document.readyState === 'complete') startHero();
  else window.addEventListener('load', startHero);

  /* ---------- 3. Scroll reveals ---------- */
  // [selector, reveal type, stagger in ms between siblings]
  var groups = [
    ['.ledger-table tbody tr', 'up', 120],
    ['.section-head', 'up', 0],
    ['.serve-item', 'up', 120],
    ['.cat', 'up', 90],
    ['.story-media', 'wipe', 0],
    ['.story-copy > h2, .story-lede', 'up', 100],
    ['.founder', 'up', 150],
    ['.story-close', 'fade', 0],
    ['.step', 'up', 130],
    ['.logistics-top > div', 'left', 0],
    ['.logistics-top > img', 'right', 0],
    ['.route li', 'up', 140],
    ['.table-wrap', 'up', 0],
    ['.check-grid article', 'up', 120],
    ['.quote-copy', 'left', 0],
    ['.quote-form', 'right', 0],
    ['.brokers-grid', 'wipe', 0],
    ['.brokers-grid > div', 'up', 0],
    ['.page-hero-copy > *', 'up', 90],
    ['.page-hero-media', 'wipe', 0],
    ['.facts dl > div', 'up', 90],
    ['.range-group', 'up', 120],
    ['.focus-list li', 'up', 60],
    ['.spec-sheet', 'right', 0],
    ['.split > *', 'up', 120],
    ['.mode', 'up', 120],
    ['.info-card', 'up', 120],
    ['.timeline li', 'left', 140],
    ['.dept', 'up', 90],
    ['.cta-band-inner > *', 'up', 120],
    ['.footer-grid > *', 'fade', 80]
  ];

  groups.forEach(function (g) {
    document.querySelectorAll(g[0]).forEach(function (el, i) {
      el.setAttribute('data-reveal', g[1]);
      // stagger resets per row of 4 so late items don't wait too long
      el.style.setProperty('--delay', ((i % 4) * g[2]) + 'ms');
    });
  });

  var revealEls = document.querySelectorAll('[data-reveal]');
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
    revealEls.forEach(function (el) { io.observe(el); });
  } else {
    revealEls.forEach(function (el) { el.classList.add('is-visible'); });
  }

  /* ---------- 4. Parallax + progress bar + header ---------- */
  var storyMedia = document.querySelector('.story-media');
  if (storyMedia) storyMedia.setAttribute('data-parallax', '');
  var parallaxEls = document.querySelectorAll('[data-parallax]');
  var heroImgWrap = document.querySelector('.hero-media');

  var bar = document.createElement('div');
  bar.className = 'scroll-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);

  var header = document.getElementById('siteHeader');
  var lastY = window.scrollY;
  var ticking = false;

  function update() {
    var y = window.scrollY;
    var vh = window.innerHeight;
    var max = document.documentElement.scrollHeight - vh;

    // progress bar
    bar.style.transform = 'scaleX(' + (max > 0 ? y / max : 0) + ')';

    // hero image drifts slower than the page
    if (heroImgWrap && y < vh * 1.2) {
      heroImgWrap.style.setProperty('--parallax', (y * 0.12) + 'px');
    }

    // other parallax images, centred on the viewport
    parallaxEls.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return;
      var progress = (r.top + r.height / 2 - vh / 2) / vh; // -1 .. 1
      el.style.setProperty('--parallax', (progress * -40).toFixed(1) + 'px');
    });

    // hide header when scrolling down past the hero, show when scrolling up
    if (header) {
      var menuOpen = document.querySelector('.main-nav.is-open');
      if (!menuOpen && y > 400 && y > lastY + 4) header.classList.add('is-hidden');
      else if (y < lastY - 4 || y < 400) header.classList.remove('is-hidden');
    }
    lastY = y;
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }

  if (lenis) lenis.on('scroll', onScroll);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  update();

  // Keep keyboard users from tabbing into a hidden header
  if (header) {
    header.addEventListener('focusin', function () { header.classList.remove('is-hidden'); });
  }
})();
